import _slicedToArray from "/opt/build/repo/web/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React from 'react';
/**
 * Like a state hook, but also exports a "throttled" value which changes at regular intervals
 * @param throttleTime How often the throttled value will update
 * @param hardValue An initial value and a live value (will set both actual and throttled values if it changes).
 * @param onChange (optional) A callback function which will receive the throttled value when it changes.
 * @returns An array for deconstruction. [the actual value, the setter for the actual value, the throttled value, a reset method to return to the hard value]
 */

export function useThrottle() {
  var throttleTime = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 500;
  var hardValue = arguments.length > 1 ? arguments[1] : undefined;
  var onChange = arguments.length > 2 ? arguments[2] : undefined;

  var _React$useState = React.useState(hardValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      actualValue = _React$useState2[0],
      setActualValue = _React$useState2[1];

  var _React$useState3 = React.useState(hardValue),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      throttledValue = _React$useState4[0],
      setThrottledValue = _React$useState4[1];

  var actualValueRef = React.useRef(actualValue);
  actualValueRef.current = actualValue;
  var timeout = React.useRef();
  var resetTimeout = React.useCallback(function () {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  }, []);
  var resetToHardValue = React.useCallback(function () {
    setActualValue(hardValue);
    setThrottledValue(hardValue);
  }, [hardValue, setActualValue, setThrottledValue]);
  React.useEffect(function () {
    resetToHardValue();
  }, [hardValue]);
  React.useEffect(function () {
    if (!timeout.current) {
      timeout.current = setTimeout(function () {
        setThrottledValue(actualValueRef.current);
        onChange === null || onChange === void 0 ? void 0 : onChange(actualValueRef.current);
        resetTimeout();
      }, throttleTime);
    }
  }, [actualValue, throttleTime, setThrottledValue, resetTimeout]);
  React.useEffect(function () {
    return function () {
      resetTimeout();
    };
  }, []);
  return [actualValue, setActualValue, throttledValue, resetToHardValue];
}